import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiSend, FiUser, FiMail, FiPhone, FiFileText, FiLayers } from "react-icons/fi";
import { submitQuoteRequest } from "../api";
import { Field, FormAlert, SubmitButton, SuccessPanel } from "./forms/formKit";

// `services` (optional): a list of service names to choose from - the window then asks "which service?" itself, starting at `serviceTitle`.
// Without it the window is about one fixed service, the one on the page it was opened from.
export default function QuoteRequestModal({ serviceTitle, services, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [chosen, setChosen] = useState(serviceTitle || "");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const choosing = Array.isArray(services) && services.length > 0;
  // The list may arrive after the window opens: until the visitor picks one, it starts at the first service on it.
  const current = choosing ? (services.includes(chosen) ? chosen : services[0]) : serviceTitle;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      await submitQuoteRequest({ ...form, service: current });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
          className="fx-card fx-card-static w-full max-w-md relative max-h-[92vh] overflow-y-auto"
        >
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          >
            <FiX size={20} />
          </button>

          {status === "success" ? (
            <SuccessPanel title="Thank you!">
              Your request for <span className="text-accent-light">{current}</span> has been received. We'll get back to you soon.
            </SuccessPanel>
          ) : (
            <>
              <h3 className="text-xl font-bold mb-1 pr-8">{choosing ? "Get a Quote" : "Request Quote & Information"}</h3>
              <p className="text-sm text-gray-400 mb-6">
                {choosing ? (
                  "Choose the service you need and tell us a bit about your project."
                ) : (
                  <>Interested in <span className="text-accent-light">{serviceTitle}</span>? Tell us a bit about your project.</>
                )}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {choosing && (
                  <Field as="select" label="Service" required icon={FiLayers} value={current} onChange={(e) => setChosen(e.target.value)}>
                    {services.map((name) => <option key={name} value={name}>{name}</option>)}
                  </Field>
                )}
                <Field label="Your name" required icon={FiUser} placeholder="e.g. Ali Khan" autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <Field label="Email address" required icon={FiMail} type="email" placeholder="you@example.com" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <Field label="Phone" icon={FiPhone} placeholder="Optional" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <Field as="textarea" label="Your requirement" icon={FiFileText} rows={3} placeholder="Tell us about your requirement..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />

                <FormAlert>{status === "error" ? "Something went wrong. Please try again." : ""}</FormAlert>

                <SubmitButton busy={status === "submitting"} icon={FiSend} className="w-full">Send Request</SubmitButton>
              </form>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
